/**
 * Planning Item types.
 *
 * This discriminator lives on both sides of the wire:
 *   - the backend validates a submitted value against the item's type and the effective
 *     Selection Policy;
 *   - the frontend selects a component from the renderer registry keyed on the same union.
 *
 * Adding a type here breaks compilation in both places until both are handled. That is the point.
 *
 * See docs/engineering/09-frontend.md and docs/engineering/06-selection-policy.md
 */

export const ITEM_TYPES = [
  /** One choice from a bounded option set. The most common type. */
  'single_option',
  /** Several choices from a bounded option set, with optional min/max. */
  'multi_option',
  /** Unstructured text — a note, an intention, a dedication. */
  'free_text',
  /** A scripture citation, resolved against the lectionary reference data. */
  'scripture_ref',
  /** A named person filling a Role. Records who serves; grants no authority. */
  'person_ref',
  /** A hymn, psalm setting, or other musical selection. */
  'music_ref',
  /** A date and time, in the celebration's own timezone. */
  'date_time',
  /** A simple yes/no decision. */
  'yes_no',
  /** A whole number — a count of servers, readers, candles. */
  'number',
  /** An uploaded file, subject to ClamAV clearance before it becomes available. */
  'attachment_ref',
] as const;

export type ItemType = (typeof ITEM_TYPES)[number];

/**
 * Item value shapes, discriminated by `type`. The backend parses into these; the frontend renders
 * from them.
 */
export type ItemValue =
  | { type: 'single_option'; option_key: string }
  | { type: 'multi_option'; option_keys: string[] }
  | { type: 'free_text'; text: string }
  | { type: 'scripture_ref'; citation: string; lectionary_key?: string }
  | { type: 'person_ref'; person_id: string; display_name: string }
  | { type: 'music_ref'; title: string; composer?: string; source_ref?: string }
  | { type: 'date_time'; local_datetime: string; timezone: string }
  | { type: 'yes_no'; value: boolean }
  | { type: 'number'; value: number }
  | { type: 'attachment_ref'; attachment_id: string };

/** Types whose permitted values come from a bounded option set the Selection Policy can narrow. */
export const OPTION_BEARING_ITEM_TYPES = [
  'single_option',
  'multi_option',
  'scripture_ref',
  'music_ref',
] as const satisfies readonly ItemType[];

export type OptionBearingItemType = (typeof OPTION_BEARING_ITEM_TYPES)[number];

export function hasOptions(type: ItemType): type is OptionBearingItemType {
  return (OPTION_BEARING_ITEM_TYPES as readonly ItemType[]).includes(type);
}

/**
 * The option keys a value refers to, across every option-bearing type.
 *
 * The four types name their key differently — `option_key`, `option_keys`, `lectionary_key`,
 * `source_ref` — and **Selection Policy must narrow all four identically.** One function, used by
 * the write path that refuses a withheld option, by the conflict scan that protects approved work,
 * and by the renderer that draws the checked box, so the four cannot drift apart. They already had:
 * a Policy withholding a reading on a `scripture_ref` item was enforced nowhere while each caller
 * looked only for `option_key`.
 *
 * Takes `unknown` and is total, because every caller is reading data — a request body, a `jsonb`
 * column — that the type system cannot vouch for. A shape it does not recognise yields no keys
 * rather than throwing: the conflict scan exists to protect approved selections, and failing it
 * closed would be failing it open.
 */
export function optionKeysOf(value: unknown): string[] {
  if (typeof value !== 'object' || value === null) return [];

  const candidate = value as {
    option_key?: unknown;
    option_keys?: unknown;
    lectionary_key?: unknown;
    source_ref?: unknown;
  };

  if (typeof candidate.option_key === 'string') return [candidate.option_key];
  if (typeof candidate.lectionary_key === 'string') return [candidate.lectionary_key];
  if (typeof candidate.source_ref === 'string') return [candidate.source_ref];

  if (Array.isArray(candidate.option_keys)) {
    return candidate.option_keys.filter((key): key is string => typeof key === 'string');
  }

  return [];
}

/**
 * Where an Item came from. Only `constraint` blocks Finalization — unset optional Items are
 * recorded as unset and carried into the export as such.
 */
export const ITEM_ORIGINS = ['constraint', 'template_default', 'optional'] as const;
export type ItemOrigin = (typeof ITEM_ORIGINS)[number];

export function blocksFinalization(origin: ItemOrigin): boolean {
  return origin === 'constraint';
}
